import { useEffect, useState } from "react";
import { Save, Trash2, Plus, Loader2, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AutoForm from "./AutoForm";
import SectionEditor from "./SectionEditor";

export default function ListManager({ name, title, description, template, headerKey, headerTitle }) {
  const [items, setItems] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = () => api.get(`/admin/list/${name}`).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [name]);

  const addItem = async () => {
    try {
      await api.post(`/admin/list/${name}`, { ...template, status: "published" });
      toast.success("যোগ হয়েছে");
      load();
    } catch { toast.error("ব্যর্থ"); }
  };

  const save = async (item) => {
    setSavingId(item.id);
    try {
      await api.put(`/admin/list/${name}/${item.id}`, item);
      toast.success("সংরক্ষিত");
    } catch { toast.error("ব্যর্থ"); }
    finally { setSavingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("মুছে ফেলবেন?")) return;
    await api.delete(`/admin/list/${name}/${id}`);
    toast.success("মুছে ফেলা হয়েছে");
    load();
  };

  const toggleStatus = (idx) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], status: arr[idx].status === "draft" ? "published" : "draft" };
    setItems(arr);
    save(arr[idx]);
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setItems(arr);
    await api.post(`/admin/list/${name}/reorder`, { ids: arr.map((x) => x.id) });
    toast.success("ক্রম পরিবর্তিত");
  };

  const update = (idx, nv) => {
    const arr = [...items];
    arr[idx] = nv;
    setItems(arr);
  };

  return (
    <div className="max-w-3xl">
      {headerKey && (
        <div className="mb-8">
          <SectionEditor sectionKey={headerKey} title={headerTitle || "বিভাগ শিরোনাম"} description="এই বিভাগের শিরোনাম ও উপ-শিরোনাম" />
        </div>
      )}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-stone-800">{title}</h1>
          {description && <p className="text-sm text-stone-500 mt-1">{description}</p>}
        </div>
        <button onClick={addItem} data-testid={`add-${name}`} className="inline-flex items-center gap-2 rounded-full bg-[var(--c-primary)] text-white px-5 py-2 text-sm font-medium hover:bg-[var(--c-secondary)] transition-colors">
          <Plus className="h-4 w-4" /> নতুন যোগ করুন
        </button>
      </div>

      {items == null ? (
        <div className="text-stone-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> লোড হচ্ছে...</div>
      ) : items.length === 0 ? (
        <p className="text-stone-400">কোনো আইটেম নেই। "নতুন যোগ করুন" চাপুন।</p>
      ) : (
        <div className="space-y-4">
          {items.map((item, idx) => (
            <div key={item.id} data-testid={`${name}-item-${idx}`} className="bg-white rounded-2xl border border-stone-200 p-5">
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-stone-100">
                <span className="font-heading font-semibold text-stone-700">{item.title || item.name || `#${idx + 1}`}</span>
                <div className="flex items-center gap-1">
                  <button onClick={() => toggleStatus(idx)} title="প্রকাশ/খসড়া" className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${item.status === "draft" ? "bg-amber-50 text-amber-600" : "bg-green-50 text-green-600"}`}>
                    {item.status === "draft" ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    {item.status === "draft" ? "খসড়া" : "প্রকাশিত"}
                  </button>
                  <button onClick={() => move(idx, -1)} className="p-1.5 text-stone-400 hover:text-stone-700"><ChevronUp className="h-4 w-4" /></button>
                  <button onClick={() => move(idx, 1)} className="p-1.5 text-stone-400 hover:text-stone-700"><ChevronDown className="h-4 w-4" /></button>
                  <button onClick={() => remove(item.id)} className="p-1.5 text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <AutoForm value={item} onChange={(nv) => update(idx, nv)} hideKeys={["status"]} />
              <div className="flex justify-end mt-4">
                <button onClick={() => save(item)} disabled={savingId === item.id} className="inline-flex items-center gap-2 rounded-full bg-stone-800 text-white px-5 py-2 text-sm font-medium hover:bg-stone-700 transition-colors disabled:opacity-60">
                  {savingId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} সংরক্ষণ
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
